import { ladderAccess, servicesHome } from '@church/data'
import { Outlet, createRootRouteWithContext, redirect } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { lazy, Suspense, type ReactNode } from 'react'
import { designToolsEnabled } from '../lib/design-tweaks.ts'
import { RouteNotFound } from '../components/RouteStates.tsx'
import { ensureMe } from '../lib/session.ts'
import { currentSite, isIdPath, isServicesPath, urlOn } from '../lib/site.ts'

/** The design editor: staging and development only, and only downloaded there. */
const DesignTweaks = designToolsEnabled
  ? lazy(() =>
      import('../components/DesignTweaks.tsx')
        .then((m) => ({ default: (): ReactNode => <m.DesignTweaks /> }))
        // A tool for designers must never take the app down with it.
        .catch(() => ({ default: (): ReactNode => null })),
    )
  : null

/**
 * Root is deliberately bare: layout belongs to the pathless groups —
 * `_auth` (centered card, signed-out) and `_authed/_shell` (sidebar app).
 */
export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // One place to sign in (id.forhim.io, Phase 12e): the apps send their sign-in
  // and account pages there, and id. sends everything else to the church app.
  // In development (no host map) nothing moves.
  beforeLoad: async ({ context, location }) => {
    const here = currentSite()
    if (here === null) return
    if (here === 'id') {
      if (location.pathname === '/') throw redirect({ to: '/apps' })
      if (!isIdPath(location.pathname)) throw redirect({ href: urlOn('church', location.href) })
      return
    }
    // admin.forhim.io is the platform console and nothing else; the console
    // lives nowhere else.
    if (here === 'admin' && !isIdPath(location.pathname)) {
      if (location.pathname === '/') throw redirect({ to: '/admin' })
      if (!location.pathname.startsWith('/admin'))
        throw redirect({ href: urlOn('church', location.href) })
      return
    }
    if (here !== 'admin' && location.pathname.startsWith('/admin'))
      throw redirect({ href: urlOn('admin', location.href) })
    // services.forhim.io is ForServices: plans, songs, teams, schedules, the
    // Music Stand. Its pages live nowhere else; everything else is ForChurch's.
    if (here === 'services' && !isIdPath(location.pathname)) {
      if (location.pathname === '/') {
        // Who reads the plans sees them; a volunteer, their own schedule.
        const me = await ensureMe(context.queryClient)
        throw redirect({ to: servicesHome(ladderAccess(me)) })
      }
      if (!isServicesPath(location.pathname))
        throw redirect({ href: urlOn('church', location.href) })
      return
    }
    if (here === 'church' && isServicesPath(location.pathname))
      throw redirect({ href: urlOn('services', location.href) })
    if (isIdPath(location.pathname) && location.pathname !== '/apps') {
      // A relative ?redirect= means this app: make it absolute before leaving.
      const search = new URLSearchParams(location.searchStr)
      const back = search.get('redirect')
      if (back?.startsWith('/') && !back.startsWith('//'))
        search.set('redirect', `${window.location.origin}${back}`)
      else if (!back) search.set('redirect', `${window.location.origin}/`)
      const query = search.toString()
      throw redirect({ href: urlOn('id', `${location.pathname}${query ? `?${query}` : ''}`) })
    }
  },
  component: () => (
    <>
      <Outlet />
      {DesignTweaks && (
        <Suspense>
          <DesignTweaks />
        </Suspense>
      )}
    </>
  ),
  notFoundComponent: RouteNotFound,
})
